/**
 * Game Over Scene - FULLY RESPONSIVE
 * 
 * Displays final score and provides instant restart.
 * Features:
 * - Score display with high score comparison
 * - Instant restart (no page reload)
 * - Ad break point for platform SDKs
 * - RESPONSIVE: Works on any screen size
 */

import Phaser from 'phaser';
import { SCENES, UI } from '../config/Constants';
import { PlatformManager } from '../sdk/PlatformManager';

export class GameOverScene extends Phaser.Scene {
  private finalScore = 0;
  private highScore = 0;
  private isNewHighScore = false;
  
  // Dynamic dimensions
  private get width(): number {
    return this.scale.width;
  }
  
  private get height(): number {
    return this.scale.height;
  }
  
  constructor() {
    super({ key: SCENES.GAME_OVER });
  }
  
  init(): void {
    this.finalScore = this.registry.get('lastScore') || 0;
    this.highScore = this.registry.get('highScore') || 0;
    this.isNewHighScore = this.finalScore >= this.highScore && this.finalScore > 0;
  }
  
  create(): void {
    console.log(`[GameOverScene] Game over with score: ${this.finalScore}`, { width: this.width, height: this.height });
    
    this.createBackground();
    this.createGameOverUI();
    this.setupInput();
    this.requestAdBreak();
    
    this.cameras.main.fadeIn(300);
  }
  
  private createBackground(): void {
    // Dark overlay - full viewport
    this.add.rectangle(this.width / 2, this.height / 2, this.width, this.height, 0x0a0a1a, 0.95);
    
    // Stars - scale count with viewport
    const graphics = this.add.graphics();
    const starCount = Math.floor((this.width * this.height) / 20000);
    for (let i = 0; i < starCount; i++) {
      const x = Phaser.Math.Between(0, this.width);
      const y = Phaser.Math.Between(0, this.height);
      const alpha = Phaser.Math.FloatBetween(0.2, 0.6);
      graphics.fillStyle(0xffffff, alpha);
      graphics.fillCircle(x, y, Phaser.Math.FloatBetween(0.5, 1.5));
    }
  }
  
  private createGameOverUI(): void {
    const centerX = this.width / 2;
    
    // Game Over title - at 15% from top
    const titleY = this.height * 0.15;
    const gameOverText = this.add.text(centerX, titleY, 'GAME OVER', {
      fontSize: '56px',
      fontFamily: UI.FONT_FAMILY,
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    
    this.tweens.add({
      targets: gameOverText,
      alpha: { from: 0, to: 1 },
      y: { from: titleY - 20, to: titleY },
      duration: 500,
      ease: 'Back.out'
    });
    
    // Score label - at 25% from top
    this.add.text(centerX, this.height * 0.25, 'SCORE', {
      fontSize: '20px',
      fontFamily: UI.FONT_FAMILY,
      color: '#888899'
    }).setOrigin(0.5);
    
    // Score value - at 32% from top
    const scoreText = this.add.text(centerX, this.height * 0.32, String(this.finalScore), {
      fontSize: '72px',
      fontFamily: UI.FONT_FAMILY,
      color: this.isNewHighScore ? '#ffcc00' : '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    
    this.tweens.add({
      targets: scoreText,
      scaleX: { from: 0.5, to: 1 },
      scaleY: { from: 0.5, to: 1 },
      duration: 400,
      ease: 'Back.out',
      delay: 300
    });
    
    // New high score indicator - at 40% from top
    if (this.isNewHighScore) {
      const newBestText = this.add.text(centerX, this.height * 0.40, '🏆 NEW BEST!', {
        fontSize: '28px',
        fontFamily: UI.FONT_FAMILY,
        color: '#ffcc00'
      }).setOrigin(0.5);
      
      this.tweens.add({
        targets: newBestText,
        scaleX: { from: 1, to: 1.1 },
        scaleY: { from: 1, to: 1.1 },
        duration: 500,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1
      });
    } else {
      this.add.text(centerX, this.height * 0.40, `Best: ${this.highScore}`, {
        fontSize: '24px',
        fontFamily: UI.FONT_FAMILY,
        color: '#888899'
      }).setOrigin(0.5);
    }
    
    this.createRetryButton();
    this.createMenuButton();
    
    // Tap to restart hint - at 90% from top
    this.add.text(centerX, this.height * 0.90, 'Tap anywhere to play again', {
      fontSize: '18px',
      fontFamily: UI.FONT_FAMILY,
      color: '#666677'
    }).setOrigin(0.5);
  }
  
  private createRetryButton(): void {
    // Position at 55% from top
    const buttonY = this.height * 0.55;
    const button = this.add.container(this.width / 2, buttonY);
    
    const bg = this.add.graphics();
    bg.fillStyle(0x00d4ff, 1);
    bg.fillRoundedRect(-100, -30, 200, 60, 12);
    bg.lineStyle(2, 0x00ff88, 0.5);
    bg.strokeRoundedRect(-100, -30, 200, 60, 12);
    
    const text = this.add.text(0, 0, '↻ RETRY', {
      fontSize: '24px',
      fontFamily: UI.FONT_FAMILY,
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    
    button.add([bg, text]);
    
    const hitArea = new Phaser.Geom.Rectangle(-100, -30, 200, 60);
    button.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
    
    button.on('pointerover', () => {
      this.tweens.add({
        targets: button,
        scaleX: 1.05,
        scaleY: 1.05,
        duration: 100
      });
    });
    
    button.on('pointerout', () => {
      this.tweens.add({
        targets: button,
        scaleX: 1,
        scaleY: 1,
        duration: 100
      });
    });
    
    button.on('pointerdown', () => {
      this.restartGame();
    });
    
    this.tweens.add({
      targets: button,
      alpha: { from: 0, to: 1 },
      y: { from: buttonY + 20, to: buttonY },
      duration: 400,
      delay: 500,
      ease: 'Back.out'
    });
  }
  
  private createMenuButton(): void {
    // Position at 65% from top
    const buttonY = this.height * 0.65;
    const button = this.add.container(this.width / 2, buttonY);
    
    const text = this.add.text(0, 0, 'Main Menu', {
      fontSize: '20px',
      fontFamily: UI.FONT_FAMILY,
      color: '#888899'
    }).setOrigin(0.5);
    
    button.add([text]);
    
    text.setInteractive({ useHandCursor: true });
    
    text.on('pointerover', () => {
      text.setColor('#ffffff');
    });
    
    text.on('pointerout', () => {
      text.setColor('#888899');
    });
    
    text.on('pointerdown', () => {
      this.goToMenu();
    });
    
    this.tweens.add({
      targets: button,
      alpha: { from: 0, to: 1 },
      duration: 400,
      delay: 600
    });
  }
  
  private setupInput(): void {
    this.time.delayedCall(500, () => {
      this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        // Check if clicking on buttons based on percentage positions
        const retryY = this.height * 0.55;
        const menuY = this.height * 0.65;
        
        const retryArea = new Phaser.Geom.Rectangle(this.width / 2 - 100, retryY - 30, 200, 60);
        const menuArea = new Phaser.Geom.Rectangle(this.width / 2 - 60, menuY - 20, 120, 40);
        
        if (!retryArea.contains(pointer.x, pointer.y) && 
            !menuArea.contains(pointer.x, pointer.y)) {
          this.restartGame();
        }
      });
      
      this.input.keyboard?.on('keydown-SPACE', () => {
        this.restartGame();
      });
      
      this.input.keyboard?.on('keydown-ENTER', () => {
        this.restartGame();
      });
      
      this.input.keyboard?.on('keydown-ESC', () => {
        this.goToMenu();
      });
    });
  }
  
  private async requestAdBreak(): Promise<void> {
    const gamesPlayed = this.registry.get('gamesPlayed') || 0;
    
    if (gamesPlayed > 0 && gamesPlayed % 3 === 0) {
      console.log('[GameOverScene] Requesting ad break...');
      await PlatformManager.getInstance().requestInterstitialAd();
    }
  }
  
  private restartGame(): void {
    console.log('[GameOverScene] Restarting game...');
    
    this.cameras.main.fadeOut(200, 0, 0, 0);
    
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENES.GAME);
    });
  }
  
  private goToMenu(): void {
    console.log('[GameOverScene] Going to menu...');
    
    this.cameras.main.fadeOut(200, 0, 0, 0);
    
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENES.MENU);
    });
  }
}

export default GameOverScene;
